import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase/config';
import { sendVerificationEmail, checkEmailVerificationStatus } from '../services/firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import placeholderImage from '../assets/images/placeholder.webp';
import '../styles/Auth.css';

const ConfirmEmail: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if the user is logged in and if their email is verified
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        // If no user is logged in, redirect to login
        navigate('/login');
        return;
      }

      // Store the user's email for display
      setUserEmail(user.email);

      // Reload the user to get the most up-to-date verification status
      user.reload().then(() => {
        setEmailVerified(user.emailVerified);
        setLoading(false);
      }).catch(err => {
        setError('Failed to check verification status: ' + err.message);
        setLoading(false);
      });
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [navigate]);

  // Periodically check for email verification status
  useEffect(() => {
    if (!loading && !emailVerified) {
      // Set up a timer to periodically check if the email has been verified
      const checkInterval = setInterval(async () => {
        const isVerified = await checkEmailVerificationStatus();
        if (isVerified) {
          setEmailVerified(true);
          clearInterval(checkInterval);
        }
      }, 3000); // Check every 3 seconds
      
      return () => clearInterval(checkInterval);
    }
  }, [loading, emailVerified]);

  // Handle resending the verification email
  const handleResendEmail = async () => {
    try {
      setResendLoading(true);
      await sendVerificationEmail();
      alert('Verification email sent! Please check your inbox and spam folder.');
    } catch (err: any) {
      // Add option to bypass verification for existing users
      if (err.code === 'auth/unauthorized-continue-uri') {
        setError(
          'We encountered an issue sending verification emails. ' +
          'Since your account was created before email verification was required, ' +
          'you can proceed to the app without verification.'
        );
      } else {
        setError('Failed to send verification email: ' + err.message);
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Refresh verification status
  const checkVerificationStatus = () => {
    setLoading(true);
    if (auth.currentUser) {
      auth.currentUser.reload().then(() => {
        setEmailVerified(auth.currentUser?.emailVerified || false);
        setLoading(false);
      }).catch(err => {
        setError('Failed to refresh verification status: ' + err.message);
        setLoading(false);
      });
    }
  };

  // If email is verified, redirect to gyms page
  if (emailVerified) {
    return <Navigate to="/gyms" />;
  }

  return (
    <div className="auth-container">
      {/* Image section for desktop */}
      <div className="auth-banner">
        <img src={placeholderImage} alt="Monkey Time" />
      </div>
      
      <div className="auth-content">
        <div className="auth-card">
          <h2 className="auth-title">Confirm Your Email Address</h2>
          
          {/* Image for mobile - shown under title */}
          <div className="mobile-auth-image">
            <img src={placeholderImage} alt="Monkey Time" />
          </div>
          
          {loading ? (
            <div className="verification-loading">
              <p>Checking verification status...</p>
            </div>
          ) : (
            <div className="verification-message">
              {error && <div className="auth-error">{error}</div>}
              
              <p>We've sent a verification email to:</p>
              <p className="user-email">{userEmail || 'your email address'}</p>
              
              <p>Please click the link in the email to verify your account before continuing.</p>
              <p>If you don't see the email, check your spam folder.</p>
              
              <p className="wrong-email-hint">
                <strong>Wrong email address?</strong> No problem! You can sign up again with the correct email.
              </p>
              
              <div className="verification-actions">
                <button 
                  onClick={handleResendEmail} 
                  className="auth-button secondary-button"
                  disabled={resendLoading}
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </button>
                <button 
                  onClick={checkVerificationStatus} 
                  className="auth-button"
                >
                  I've Verified My Email
                </button>
                
                {/* Option to go back and sign up with different email */}
                <button 
                  onClick={() => navigate('/signup')} 
                  className="auth-button tertiary-button"
                >
                  Try with a Different Email Address
                </button>
                
                {/* Add a bypass option for existing users */}
                {error && error.includes('before email verification was required') && (
                  <button 
                    onClick={() => navigate('/gyms')} 
                    className="auth-button"
                  >
                    Continue to App
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmail;